import { useState } from "react";
import Tooltip from "@mui/material/Tooltip";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import { useCurrentUser } from "../../../hooks/useCurrentUser";
import { classifySource, RepeatIcon } from "./sourceIcons";
import "./SourceBadge.css";

const HIDDEN_FOR_ROLES = ["institute_user", "institute_admin", "channel_user"];

export const tooltipProps = {
  arrow: true,
  placement: "top",
  componentsProps: {
    tooltip: {
      sx: {
        backgroundColor: "#101828",
        color: "#fff",
        borderRadius: "12px",
        padding: "10px 14px",
        maxWidth: "none",
        boxShadow: "0 10px 24px rgba(0,0,0,.22)",
      },
    },
    arrow: { sx: { color: "#101828" } },
  },
};

export const Details = ({ title, rows }) => (
  <div className="source-badge-tip">
    <div className="source-badge-tip__title">{title}</div>
    <dl>
      {rows.map(([label, value]) => (
        <div key={label} className="source-badge-tip__row">
          <dt>{label}</dt>
          <dd>{value || "-"}</dd>
        </div>
      ))}
    </dl>
  </div>
);

const CopyableValue = ({ value }) => {
  const [copied, setCopied] = useState(false);
  if (!value) return "-";

  const handleCopy = (e) => {
    e.stopPropagation();
    navigator.clipboard
      .writeText(value)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      })
      .catch(() => {});
  };

  return (
    <span className="source-badge-tip__copy">
      {value}
      <button type="button" onClick={handleCopy} aria-label={copied ? "Copied" : "Copy loan ID"} title={copied ? "Copied" : "Copy"}>
        {copied ? <CheckIcon sx={{ fontSize: 14 }} /> : <ContentCopyIcon sx={{ fontSize: 14 }} />}
      </button>
    </span>
  );
};

// Source + repeat-customer icons shown after the merchant name on personal loan rows.
export default function SourceBadge({ attribution, repeat }) {
  const decode = useCurrentUser();
  if (HIDDEN_FOR_ROLES.includes(decode?.role)) return null;

  const source = classifySource(attribution?.sourcePlatform || attribution?.mediaSource, attribution);
  if (!source && !repeat) return null;

  return (
    <span className="source-badge">
      {source &&
        (source.type === "organic" ? (
          <Tooltip {...tooltipProps} title={<span className="source-badge-tip__title source-badge-tip__title--only">Organic</span>}>
            <span className="source-badge__icon" tabIndex={0} aria-label={source.label}>
              <source.Icon />
            </span>
          </Tooltip>
        ) : (
          <Tooltip
            {...tooltipProps}
            title={
              <Details
                title={source.label}
                rows={[
                  ...(source.partner ? [["Partner", source.partner]] : []),
                  // Sub-affiliate traffic: the agency, and the affiliate it came through
                  ...(source.agency
                    ? [["Agency", source.agency], ["Affiliate", attribution.mediaSource || attribution.sourcePlatform]]
                    : [["Media source", attribution.mediaSource || attribution.sourcePlatform]]),
                  ["Campaign", attribution.campaignName],
                  ["Adset", attribution.adsetName],
                ]}
              />
            }
          >
            <span className="source-badge__icon source-badge__icon--hover" tabIndex={0} aria-label={source.label}>
              <source.Icon />
            </span>
          </Tooltip>
        ))}
      {repeat && (
        <Tooltip
          {...tooltipProps}
          title={
            <Details
              title="Repeat customer"
              rows={[
                ["Previous loans", repeat.previousLoanCount],
                ["Last loan ID", <CopyableValue value={repeat.lastLoanId} />],
              ]}
            />
          }
        >
          <span className="source-badge__icon source-badge__icon--repeat source-badge__icon--hover" tabIndex={0} aria-label="Repeat customer">
            <RepeatIcon />
          </span>
        </Tooltip>
      )}
    </span>
  );
}
